import { difference, omit } from "lodash";
import { all, call, put, select, takeEvery } from "redux-saga/effects";

import { DEFAULT_HTML_TEMPLATE, DEFAULT_HTML_THREADED_TEMPLATE } from "constants/settings/emailTemplates";
import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { pushWithUTM } from "helpers/sagas";
import { SettingsRoutes } from "routes/settings/constants";
import { ApiManager } from "services/api";
import * as EmailTemplateSerializer from "services/api/email-templates/email-template-serializer";
import { ToastAutoHideDelay, ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";

import { IEntityCreate, IEntityDelete, IEntityUpdate, IRequestFailure } from "../actions";
import { ILicense, selectLicense } from "../license";
import { ITeam, selectTeamsAlphabetically } from "../teams";

import { EmailTemplateActionNames, EmailTemplateActions } from "./actions";
import { IEmailTemplate, IPreviewEmailTemplatePayload } from "./interfaces";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchTemplates() {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailTemplateApi.fetch);

  if (response.result) {
    const templates = response.result.map(EmailTemplateSerializer.deserialize);
    const license: ILicense = yield select(selectLicense);
    const htmlTemplate = license.settings.ticketThread ? DEFAULT_HTML_THREADED_TEMPLATE : DEFAULT_HTML_TEMPLATE;

    yield put(EmailTemplateActions.fetchSuccess({ values: templates, defaultHtmlTemplate: htmlTemplate }));
  } else {
    yield put(EmailTemplateActions.fetchFailure({ error: response.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* update(action: IActionWithPayload<string, IEntityUpdate<IEmailTemplate & { teamIDs: string[] }>>) {
  const { value } = action.payload;
  const { teamIDs } = value;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const teams = yield select(selectTeamsAlphabetically);
  const teamsWithTemplate = teams.filter((team: ITeam) => team.templateId === value.id).map((team: ITeam) => team.id);
  // @ts-expect-error TS(2345): Argument of type 'string[] | undefined' is not ass... Remove this comment to see the full error message
  const idsToRemove = difference(teamsWithTemplate, teamIDs);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let removedResponse: Record<string, any>;
  if (idsToRemove.length) {
    removedResponse = yield call(ApiManager.teamApi.batchUpdate, { fields: { templateID: null }, ids: idsToRemove });
  }

  // @ts-expect-error TS(2454): Variable 'removedResponse' is used before being as... Remove this comment to see the full error message
  if (!removedResponse?.result && idsToRemove.length) {
    // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    yield put(EmailTemplateActions.updateFailure({ id: value.id, error: removedResponse?.error }));
    return;
  }

  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailTemplateApi.update, omit(value, "teamIDs"));
  let teamsResponse;

  if (teamIDs?.length)
    // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
    teamsResponse = yield call(ApiManager.teamApi.batchUpdate, {
      fields: { templateID: value.id },
      ids: teamIDs,
    });
  if (response.result && (teamsResponse?.result || !teamIDs?.length)) {
    const template = EmailTemplateSerializer.deserialize(response.result);
    yield put(EmailTemplateActions.updateSuccess({ id: template.id, value: template }));
    yield put(pushWithUTM(SettingsRoutes.EmailTemplates));
  } else {
    // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    yield put(EmailTemplateActions.updateFailure({ id: value.id, error: response.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_TEMPLATE_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_TEMPLATE_UPDATE_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* test(
  action: IActionWithPayload<string, { emails: string[]; content: IEmailTemplate["content"]; type: string }>
) {
  const { emails, content, type } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailTemplateApi.test, emails, content, type);
  if (response.result) {
    yield put(EmailTemplateActions.testSuccess());
  } else {
    yield put(EmailTemplateActions.testFailure(response.error));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* testSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_TEMPLATE_TEST_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* testFailure(action: IActionWithPayload<string, { error: { type: string; message: string } }>) {
  const { type, message } = action.payload.error;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_TEMPLATE_TEST_FAILURE, { type, message }),
      variant: ToastVariant.Error,
      autoHideDelayTime: type === "limitExceeded" ? ToastAutoHideDelay.Infinite : ToastAutoHideDelay.Long,
      error: action?.payload?.error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* create(action: IActionWithPayload<string, IEntityCreate<IEmailTemplate & { teamIDs: string[] }>>) {
  const { value } = action.payload;
  const { teamIDs } = value;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailTemplateApi.create, omit(value, "teamIDs"));
  let teamsResponse: { result?: ITeam; error: IRequestFailure };

  if (teamIDs?.length) {
    teamsResponse = yield call(ApiManager.teamApi.batchUpdate, {
      fields: { templateID: response?.result?.ID },
      ids: teamIDs,
    });
  }

  // @ts-expect-error TS(2454): Variable 'teamsResponse' is used before being assi... Remove this comment to see the full error message
  if (response.result && (teamsResponse?.result || teamIDs?.length === 0)) {
    yield put(
      EmailTemplateActions.createSuccess({
        id: response.result.id,
        value: EmailTemplateSerializer.deserialize(response.result),
      })
    );
    yield put(pushWithUTM(SettingsRoutes.EmailTemplates));
  } else {
    yield put(EmailTemplateActions.createFailure({ error: response.error?.error }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createFailure(action: IActionWithPayload<string, { error: { type: string; message: string } }>) {
  const { type } = action.payload.error;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_TEMPLATE_CREATE_FAILURE, { type }),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_TEMPLATE_CREATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* preview(action: IActionWithPayload<string, IPreviewEmailTemplatePayload>) {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailTemplateApi.fetchPreview, action.payload);

  if (response.result) {
    yield put(EmailTemplateActions.fetchPreviewSuccess(response.result));
  } else {
    yield put(EmailTemplateActions.fetchPreviewFailure({ id: "00000", error: "Failed to fetch preview" }));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteTemplate(action: IActionWithPayload<string, IEntityDelete>) {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.emailTemplateApi.deleteTemplate, action.payload.id);

  if (response.result) {
    yield put(EmailTemplateActions.deleteSuccess(response.result));
  } else {
    yield put(EmailTemplateActions.deleteFailure(response.result));
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_TEMPLATE_DELETE_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_TEMPLATE_DELETE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
  yield call(trackEngagement, "Email templates: Delete template", EventPlace.Settings);
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* emailTemplateSagas() {
  yield all([
    takeEvery(EmailTemplateActionNames.FETCH_EMAIL_TEMPLATE_REQUEST, fetchTemplates),
    takeEvery(EmailTemplateActionNames.UPDATE_EMAIL_TEMPLATE_REQUEST, update),
    takeEvery(EmailTemplateActionNames.UPDATE_EMAIL_TEMPLATE_SUCCESS, updateSuccess),
    takeEvery(EmailTemplateActionNames.UPDATE_EMAIL_TEMPLATE_FAILURE, updateFailure),
    takeEvery(EmailTemplateActionNames.TEST_EMAIL_TEMPLATE_REQUEST, test),
    takeEvery(EmailTemplateActionNames.TEST_EMAIL_TEMPLATE_SUCCESS, testSuccess),
    takeEvery(EmailTemplateActionNames.TEST_EMAIL_TEMPLATE_FAILURE, testFailure),
    takeEvery(EmailTemplateActionNames.CREATE_EMAIL_TEMPLATE_REQUEST, create),
    takeEvery(EmailTemplateActionNames.CREATE_EMAIL_TEMPLATE_SUCCESS, createSuccess),
    takeEvery(EmailTemplateActionNames.CREATE_EMAIL_TEMPLATE_FAILURE, createFailure),
    takeEvery(EmailTemplateActionNames.FETCH_PREVIEW_REQUEST, preview),
    takeEvery(EmailTemplateActionNames.DELETE_EMAIL_TEMPLATE_REQUEST, deleteTemplate),
    takeEvery(EmailTemplateActionNames.DELETE_EMAIL_TEMPLATE_SUCCESS, deleteSuccess),
    takeEvery(EmailTemplateActionNames.DELETE_EMAIL_TEMPLATE_FAILURE, deleteFailure),
  ]);
}
